import { useContext, useMemo } from "react";
import { Context } from "../../context/Context";
import { getPolicy, policySignature } from "../core/policy";

/**
 * The only way components read authorization.
 * Auth state stays in the existing Context; this derives decisions from it,
 * memoized per identity so nothing is recomputed or re-fetched per check.
 *
 *   const { can, role, isAccessReady } = useAccess();
 *   can("menu.send", { restaurantId })   // execution: role + resource scope
 *   has("menu.send")                      // visibility: role only (scope ignored)
 */
export function useAccess() {
  const context = useContext(Context);
  const isAuthenticated = Boolean(context?.isAuthenticated);
  const user = context?.currentUserInfo ?? null;
  const signature = policySignature({ isAuthenticated, user });

  return useMemo(() => {
    const policy = getPolicy({ isAuthenticated, user });
    return {
      role: policy.role,
      status: policy.status,
      isAccessReady: policy.isReady,
      needsBranchAssignment: policy.needsBranchAssignment,
      isAuthenticated: policy.isAuthenticated,
      can: policy.can,
      has: policy.has,
      canAny: policy.canAny,
      canAll: policy.canAll,
      explain: policy.evaluate,
    };
    // `signature` captures every field the policy reads from `user`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}

export default useAccess;
